import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { ChangeSelectedThermalOperation, ChangeSelectedThermalOperationType, SyncPressAndTempResults } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { BaseOperation } from '../../models/thermal-operation.model';
import { PressureResults, PressureResultPhase, PressureResultsUi } from '../../models/thermal-results.model';
import { ThermalOperationsService } from '../../services/thermal-operations.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { zipObject } from 'lodash-es';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIds } from 'src/app/core/components/user-admin-page/user-model';
import { formatUnitNumber } from 'src/app/shared/helpers/unit-number-format.helper';

@Component({
  selector: 'app-thermal-pressure-results',
  templateUrl: './thermal-pressure-results.component.html',
  styles: [``],
  standalone: false
})
export class ThermalPressureResultsComponent implements OnInit, OnDestroy {

  public isLoading = signal<boolean>(true);
  public missingOperations = false;
  public needToCalculate: boolean;
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public pressUnit: string;
  public depthUnit: string;
  public shortLengthUnit = 'in';
  public readonly formatAnnulusOption = (option: { label: string }): string => {
    const unit = this.shortLengthUnit;
    return option.label.replace(/\d+\.\d+/g, value =>
      formatUnitNumber(Number(value), unit, undefined, undefined, { [unit]: unit === 'in' ? 3 : 2 })
    );
  };
  public plotName: string;

  public singleMultiple: SelectItem[] = [
    { label: 'Single', value: 'singleOperation' },
    { label: 'Multiple', value: 'multipleOperation' },
    { label: 'Operation Type', value: 'operationType' }
  ];

  public operationTypes: SelectItem[] = [
    { label: 'Production', value: 'production' },
    { label: 'Injection', value: 'injection' },
    { label: 'Circulation', value: 'circulation' },
    { label: 'Trip Pipe & Circulate', value: 'tripPipeAndCirculate' },
    { label: 'Run Casing & Circulate', value: 'runCasingAndCirculate' },
    { label: 'Drilling', value: 'drilling' },
    { label: 'Cementing', value: 'cementing' },
    { label: 'Shut In', value: 'shutIn' },
    { label: 'Screenout', value: 'screenout' }
  ];
  public selectedOperationType = 'drilling';

  public multipleResultsFull: any[] = [];
  public annulusNames: { label: string, value: string }[] = [];
  public operationNames: any[] = [];
  public operationNamesPlot: { field: string, header: string }[] = [];
  public selectedResultTypeIndex: number;

  public operations: Partial<BaseOperation>[];
  public allOperationsResults: PressureResults[];
  public results: any[];
  public resultsGrid: any[];
  public cols: any[];
  public phases: PressureResultPhase[];
  public calculationError = "Please calculate PERICAL to see results.";
  public depthView: SelectItem[];
  public selectedOperationIndex = 0;
  public moveUpDisabled = true;
  public moveDownDisabled = false;
  public selectedStringId: string;

  public yAxisTitle = '';
  public xAxisTitle = '';

  private _subscriptions: Subscription = new Subscription();

  // State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.pressureResultsStore = this._storeService.createStore(this.componentId, new PressureResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedSingleMultiple = this.pressureResultsStore.state.selectedSingleMultiple || 'singleOperation';
    this.selectedResultsDisplay = this.pressureResultsStore.state.selectedResultsDisplay || 'plot';
    this.selectedDepthView = this.pressureResultsStore.state.selectedDepthView || 'md';
    this.syncThermalResults = this.pressureResultsStore.state.syncThermalResults ?? true;
    this.selectedOperation = this.pressureResultsStore.state.selectedOperation;
    this.selectedAnnulus = this.pressureResultsStore.state.selectedAnnulus;
    this.selectedPhase = this.pressureResultsStore.state.selectedPhase;
  }
  get componentId(): string {
    return this._componentId;
  }
  public pressureResultsStore: Store;

  // Local properties for store state (Stage 1 migration)
  public selectedSingleMultiple: string = 'singleOperation';
  public selectedResultsDisplay: string = 'plot';
  public selectedDepthView: string = 'md';
  public syncThermalResults: boolean = true;
  public selectedOperation: Partial<BaseOperation>;
  public selectedAnnulus: any;
  public selectedPhase: PressureResultPhase;

  constructor(
    private _thermalOperationsService: ThermalOperationsService,
    private _messenger: MediatorService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) {
    this._subscriptions.add(this._messenger.of(ChangeSelectedThermalOperation).subscribe((e) => {
      if (this.selectedSingleMultiple === 'multipleOperation') {
        return;
      }
      let selectedOperation;
      if (!e.selectedThermalOperation.value?.['phaseNumber']) {
        selectedOperation = e.selectedThermalOperation;
        this.selectedOperation = selectedOperation.value;
        this.pressureResultsStore.update(state => ({ ...state, selectedOperation: selectedOperation.value }));

        if (!this.selectedSingleMultiple) {
          this.selectedSingleMultiple = 'singleOperation';
          this.pressureResultsStore.update(state => ({ ...state, selectedSingleMultiple: 'singleOperation' }));
        };

        this.onOperationSelected(selectedOperation, false);
      } else {
        this.selectedPhase = this.allOperationsResults.find(x => x.operationId === this.selectedOperation.id).phases.find(x => x.phaseNumber === e.selectedThermalOperation.value['phaseNumber']);
        this.pressureResultsStore.update(state => ({
          ...state,
          selectedPhase: this.selectedPhase
        }));
        selectedOperation = this.selectedOperation;

        if (!this.selectedSingleMultiple) {
          this.selectedSingleMultiple = 'singleOperation';
          this.pressureResultsStore.update(state => ({ ...state, selectedSingleMultiple: 'singleOperation' }));
        };

        this.onOperationSelected(selectedOperation, false, this.selectedPhase);
      }
    }));

    this._subscriptions.add(this._messenger.of(SyncPressAndTempResults).subscribe((e) => {
      this.syncThermalResults = e.syncPressAndTemp;
      this.pressureResultsStore.update(state => ({ ...state, syncThermalResults: e.syncPressAndTemp }));
    }));

    this._subscriptions.add(this._messenger.of(ChangeSelectedThermalOperationType).subscribe(({ operationType }) => {
      if (this.selectedSingleMultiple !== 'operationType' || this.selectedOperationType === operationType) {
        return;
      }

      this.selectedOperationType = operationType;
      this.remapMultipleResults(0);
      this.plotName = this.setPlotName();
    }));
  }

  async ngOnInit(): Promise<void> {
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.pressUnit = uu.pressure;
    this.depthUnit = uu.longLengths;
    this.shortLengthUnit = uu.shortLengths;

    if (!this.selectedSingleMultiple) {
      this.selectedSingleMultiple = 'singleOperation';
      this.pressureResultsStore.update(state => ({ ...state, selectedSingleMultiple: 'singleOperation' }));
    };

    this.selectedStringId = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS).tubularId;
    this.getThermalPressureResults(this.selectedSingleMultiple === 'operationType');

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.depthView = [
      { label: 'MD', value: 'md' },
      { label: 'TVD', value: 'tvd' }
    ];

    // this.selectedDepthView = 'md';
  }

  private signalRfunc(data: any): void {
    if (data.action == PeriforOnChangeMessages.REFRESH_THERMAL_RESULTS || data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.isLoading.set(true);

      this.selectedStringId = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS).tubularId;

      this.getThermalPressureResults(this.selectedSingleMultiple === 'operationType');
      if (this.selectedSingleMultiple === 'multipleOperation') {
        this.selectedAnnulus = this.annulusNames[0];
        this.pressureResultsStore.update(state => ({ ...state, selectedAnnulus: this.annulusNames[0] }));
      }
    }
  }

  private getThermalPressureResults(getWholeDesignResults = false): void {
    const sources = [
      this._thermalOperationsService.getThermalOperations(),
      this._thermalOperationsService.getThermalPressureResults(false, getWholeDesignResults) as Observable<any>
    ];

    forkJoin(sources).subscribe({
      next: ([operations, allResults]) => {
        this.operations = operations;
        if (!this.selectedOperation || !this.operations.find(o => o.id === this.selectedOperation.id)) {
          this.selectedOperation = this.operations[0];
          this.pressureResultsStore.update(state => ({ ...state, selectedOperation: this.operations[0] }));
        }

        if (this.operations.length === 0) {
          this.missingOperations = true;
        } else {
          this.missingOperations = false;
        }

        if (this.missingOperations) {
          this.results = [];
        }

        if (this.operations.length == 0) {
          this.isLoading.set(false);
          this.missingOperations = true;
          return;
        }

        this.allOperationsResults = allResults;

        if (this.allOperationsResults.length > 0) {
          if (this.allOperationsResults.length < 2) {
            this.moveUpDisabled = true;
            this.moveDownDisabled = true;
          }

          if (this.selectedSingleMultiple == 'singleOperation') {
            const selectedOperation = this.allOperationsResults.find(x => x.operationId === this.selectedOperation.id);
            this.selectedPhase = selectedOperation?.phases[selectedOperation.phases.length - 1];
            this.pressureResultsStore.update(state => ({
              ...state,
              selectedPhase: this.selectedPhase
            }));
            this.plotName = this.setPlotName();
            this.remapResults();
          } else {
            this.remapMultipleResults(0);
            this.plotName = this.setPlotName();
          }
        } else {
          this.clearResults();
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.missingOperations = true;
        this.isLoading.set(false);
      }
    });
  }

  setPlotName(): string {
    if (this.selectedSingleMultiple === 'singleOperation') {
      return 'thermalPressureResults' + this.selectedResultsDisplay + ' - ' +
        this.selectedOperation.id + ' ' + this.selectedPhase?.phaseNumber;
    } else {
      return 'thermalPressureResults' + ' - ' + this.selectedStringId + ' - ' + this.selectedResultsDisplay + ' - ' +
        this.selectedAnnulus?.label;
    }
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.pressureResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  public onSelectResultsDisplay(e): void {
    this.selectedResultsDisplay = e.value;
    this.pressureResultsStore.update(state => ({
      ...state,
      selectedResultsDisplay: e.value
    }));
    this.plotName = this.setPlotName();
  }

  public syncPressAndTemp(e) {
    this.syncThermalResults = e.checked;
    this.pressureResultsStore.update(state => ({ ...state, syncThermalResults: e.checked }));
    this._messenger.publish(new SyncPressAndTempResults(this.syncThermalResults));
    this._messenger.publish(new ChangeSelectedThermalOperation(this.selectedOperation));
  }

  onOperationSelected(e, isCalledFromHere, selectedPhase?: PressureResultPhase): void {
    if (isCalledFromHere && this.syncThermalResults) {
      this._messenger.publish(new ChangeSelectedThermalOperation(e));
    }
    this.moveUpDisabled = false;
    this.moveDownDisabled = false;
    if (this.allOperationsResults[0].operationId === (e.value?.id ?? e.id)) {
      this.moveUpDisabled = true;
    }
    if (this.allOperationsResults[this.allOperationsResults.length - 1].operationId === (e.value?.id ?? e.id)) {
      this.moveDownDisabled = true;
    }
    if (this.allOperationsResults.length > 0) {
      const firstOperation = this.allOperationsResults.find(x => x.operationId === (e.value?.id ?? e.id));
      this.selectedOperation = e.value ?? e;
      this.selectedPhase = !selectedPhase ? firstOperation?.phases[firstOperation.phases.length - 1] : selectedPhase;
      this.pressureResultsStore.update(state => ({
        ...state,
        selectedOperation: this.selectedOperation,
        selectedPhase: this.selectedPhase
      }));
      this.plotName = this.setPlotName();
      this.remapResults(selectedPhase);
    } else {
      this.needToCalculate = true;
    }
  }

  onUpDownButtonClick(e, isCalledFromHere) {
    this.moveUpDisabled = false;
    this.moveDownDisabled = false;
    if (this.selectedSingleMultiple === 'singleOperation') {
      this.moveSingleOperations(e, isCalledFromHere);
    } else {
      this.moveMultipleOperations(e);
    }
  }
  private moveMultipleOperations(e: 'up' | 'down') {
    this.annulusNames = this.selectedPhase.pressureResultsTable.headers.slice(2).map(h => {
      const cleanLabel = h
        .replace(/"/g, '\u2033'); // Replace double quotes with ″ (prime symbol)

      return {
        value: toCamelCaseString(h),
        label: cleanLabel
      };
    });

    const offset = e === 'up' ? -1 : 1;
    this.moveSelectedAnnulus(offset);

    this.moveUpDisabled = this.annulusNames.findIndex(a => a.value === this.selectedAnnulus?.value) === 0;
    this.moveDownDisabled = this.annulusNames.findIndex(a => a.value === this.selectedAnnulus?.value) === this.annulusNames.length - 1;
  }

  private moveSelectedAnnulus(offset: number): void {
    const currentValue = this.selectedAnnulus?.value;

    if (!currentValue || !this.annulusNames?.length) return;

    const currentIndex = this.annulusNames.findIndex(x => x.value === currentValue);
    const targetIndex = currentIndex + offset;

    if (targetIndex < 0 || targetIndex >= this.annulusNames.length) return;

    const matched = this.annulusNames[targetIndex];

    this.selectedAnnulus = matched;
    this.pressureResultsStore.update(state => ({
      ...state,
      selectedAnnulus: matched
    }));

    this.plotName = this.setPlotName();
    this.remapMultipleResults(0);
  }

  private moveSingleOperations(e, isCalledFromHere) {
    this.selectedOperationIndex = this.operations.findIndex(x => x.id === this.selectedOperation.id);
    if (e === 'up') {
      const selectedOperation = this.operations[this.selectedOperationIndex - 1];
      if (!selectedOperation) {
        this.moveUpDisabled = true;
        return;
      }
      if (isCalledFromHere && this.syncThermalResults) {
        this._messenger.publish(new ChangeSelectedThermalOperation(selectedOperation));
      }
      this.selectedOperation = selectedOperation;
      this.pressureResultsStore.update(state => ({ ...state, selectedOperation: selectedOperation }));
      this.selectedOperationIndex = this.operations.findIndex(x => x.id === this.selectedOperation.id);
      this.moveUpDisabled = this.selectedOperationIndex === 0 ? true : false;
      this.moveDownDisabled = this.selectedOperationIndex === this.operations.length - 1 ? true : false;
      const firstOperation = this.allOperationsResults[this.selectedOperationIndex];
      if (!firstOperation) {
        this.clearResults();
      }
      this.selectedPhase = firstOperation?.phases[firstOperation.phases.length - 1];
      this.pressureResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
      this.remapResults();
    } else {
      const selectedOperation = this.operations[this.selectedOperationIndex + 1];
      if (!selectedOperation) {
        this.moveDownDisabled = true;
        return;
      }
      if (isCalledFromHere && this.syncThermalResults) {
        this._messenger.publish(new ChangeSelectedThermalOperation(selectedOperation));
      }
      this.selectedOperation = selectedOperation;
      this.pressureResultsStore.update(state => ({ ...state, selectedOperation: selectedOperation }));
      this.selectedOperationIndex = this.operations.findIndex(x => x.id === this.selectedOperation.id);
      this.moveUpDisabled = this.selectedOperationIndex === 0 ? true : false;
      this.moveDownDisabled = this.selectedOperationIndex === this.operations.length - 1 ? true : false;
      const firstOperation = this.allOperationsResults[this.selectedOperationIndex];
      if (!firstOperation) {
        this.clearResults();
      }
      this.selectedPhase = firstOperation?.phases[firstOperation.phases.length - 1];
      this.pressureResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
      this.remapResults();
      this.plotName = this.setPlotName();
    }
  }

  private clearResults() {
    this.moveDownDisabled = true;
    this.moveUpDisabled = true;
    this.results = [];
    this.cols = [];
    this.resultsGrid = [];
    this.multipleResultsFull = [];
    this.needToCalculate = true;
    this.plotName = null;
    return
  }

  public onPhaseSelected(results: any[], isCalledFromHere: boolean): void {
    if (isCalledFromHere && this.syncThermalResults) {
      this._messenger.publish(new ChangeSelectedThermalOperation(results));
    }
    if (this.allOperationsResults.length > 0) {
      this.selectedPhase = results['value'];
      this.pressureResultsStore.update(state => ({ ...state, selectedPhase: results['value'] }));
      this.plotName = this.setPlotName();
      this.remapResults(results['value']);
    } else {
      this.needToCalculate = true;
    }
  }

  public modeToggle(e) {
    this.moveUpDisabled = true;
    this.moveDownDisabled = false;
    this.selectedSingleMultiple = e;
    this.pressureResultsStore.update(state => ({ ...state, selectedSingleMultiple: e }));

    if (e === 'singleOperation') {
      this.syncThermalResults = true;
      this.pressureResultsStore.update(state => ({ ...state, syncThermalResults: true }));
      this._messenger.publish(new SyncPressAndTempResults(this.syncThermalResults));
      this.selectedOperation = this.operations[0];
      this.pressureResultsStore.update(state => ({
        ...state,
        selectedOperation: this.operations[0]
      }));
      this.remapResults(null);
    } else if (e === 'multipleOperation') {
      this.syncThermalResults = false;
      this.pressureResultsStore.update(state => ({ ...state, syncThermalResults: false }));
      this.remapMultipleResults(0);
    } else {
      this.syncThermalResults = false;
      this.pressureResultsStore.update(state => ({ ...state, syncThermalResults: false }));
      this.isLoading.set(true);
      this.getThermalPressureResults(true);
    }
    this.plotName = this.setPlotName();
  }

  public annulusDropdownChange(e) {
    this.moveUpDisabled = false;
    this.moveDownDisabled = false;
    this.selectedAnnulus = e.value;
    this.plotName = this.setPlotName();
    this.pressureResultsStore.update(state => ({ ...state, selectedAnnulus: e.value }));
    this.remapMultipleResults(0);
  }

  public onOperationTypeSelected(operationType: string): void {
    this.selectedOperationType = operationType;
    this.remapMultipleResults(0);
    this.plotName = this.setPlotName();
    this._messenger.publish(new ChangeSelectedThermalOperationType(operationType));
  }

  public remapMultipleResults(result: any, selectedPhase?: PressureResultPhase): void {
    const matchingOperationTypeResults = this.selectedSingleMultiple === 'operationType'
      ? this.allOperationsResults.filter(ar => ar.phases[0] && (
        ar.operationType === this.selectedOperationType ||
        this.operations.some(operation => operation.id === ar.operationId && operation.inside?.type === this.selectedOperationType)
      ))
      : null;
    let allOperationsResults = matchingOperationTypeResults ?? this.allOperationsResults.filter(ar => this.operations.find(rm => rm.name === ar.name));

    this.selectedOperation = allOperationsResults[0];
    this.pressureResultsStore.update(state => ({ ...state, selectedOperation: allOperationsResults[0] }));
    const selectedOperationResults = allOperationsResults[0];

    this.needToCalculate = !selectedOperationResults;
    this.phases = selectedOperationResults ? selectedOperationResults.phases : null;
    if (!selectedOperationResults) {
      this.results = [];
      this.cols = [];
      this.resultsGrid = [];
      this.multipleResultsFull = [];
      if (this.selectedSingleMultiple === 'operationType') {
        const operationTypeName = this.operationTypes.find(type => type.value === this.selectedOperationType)?.label ?? '';
        this.calculationError = `Please make sure that ${operationTypeName} operations are calculated.`;
      }
      return;
    }
    if (!selectedPhase) {
      selectedPhase = this.phases ? this.phases[this.phases.length - 1] : null;
      this.selectedPhase = selectedPhase;
      this.pressureResultsStore.update(state => ({ ...state, selectedPhase: selectedPhase }));
    }

    if (!selectedPhase) {
      this.results = [];
      this.cols = [];
      this.resultsGrid = [];
      this.multipleResultsFull = [];
      this.needToCalculate = true;
      return;
    }

    if (this.operations.length === 0) {
      this.missingOperations = true;
    }

    this.selectedResultTypeIndex = result.value ?? result;

    this.annulusNames = this.selectedPhase.pressureResultsTable.headers.slice(2).map(h => {
      const cleanLabel = h
        .replace(/"/g, '\u2033'); // Replace double quotes with ″ (prime symbol)

      return {
        value: toCamelCaseString(h),
        label: cleanLabel
      };
    });

    allOperationsResults = allOperationsResults.sort((a, b) => {
      return this.operations.findIndex(x => a.name == x.name) - this.operations.findIndex(x => b.name == x.name);
    });

    this.operationNames = allOperationsResults.map(x => {
      return {
        field: toCamelCaseString(x.name),
        header: x.name,
        valueFormatter: (params: any) => this.formatDecimal(params)
      };
    });

    this.multipleResultsFull = [];

    if (this.operations.length === 0) {
      this.missingOperations = true;
    }

    let longestOperationDataIndex = 0;
    let dataLength = 0;
    for (let l = 0; l < allOperationsResults.length; l++) {
      if (allOperationsResults[l].phases.length == 0) {
        continue;
      }
      const length = allOperationsResults[l].phases[0].pressureResultsTable.rows.length;
      if (dataLength < length) {
        longestOperationDataIndex = l;
        dataLength = length;
      }
    }

    const longestOpIndex = allOperationsResults[longestOperationDataIndex];
    const depths = longestOpIndex.phases[0].pressureResultsTable.rows.map(x => x[0]);

    let operationCount = 0;
    let i = 0;
    const longestOpPhaseIndex = longestOpIndex.phases.length - 1;
    for (let l = 0; l < longestOpIndex.phases[0].pressureResultsTable.rows.length; l++) {
      const multipleResults = [];
      // let lastPhase;
      for (i = 0; i < allOperationsResults.length; i++) {
        const allOperationResultsPhaseAtIndex = allOperationsResults[i].phases;
        const lastPhaseNew = allOperationResultsPhaseAtIndex.length - 1;
        if (allOperationResultsPhaseAtIndex.length == 0) {
          continue;
        }
        // lastPhase = allOperationsResults[operationCount].phases.length - 1;
        if (i === 0) {
          multipleResults.push(longestOpIndex.phases[longestOpIndex.phases.length - 1].pressureResultsTable.rows[l][0]);
          multipleResults.push(longestOpIndex.phases[longestOpIndex.phases.length - 1].pressureResultsTable.rows[l][1]);
        }

        let annulusColumnIndex = allOperationResultsPhaseAtIndex[lastPhaseNew].pressureResultsTable.headers.findIndex(x => toCamelCaseString(x) == (this.selectedAnnulus?.value || this.annulusNames[0].value));
        if (annulusColumnIndex < 0) {
          annulusColumnIndex = 2;
        }

        if (l < longestOpIndex.phases[longestOpPhaseIndex].pressureResultsTable.rows.length) {
          const currentDepth = longestOpIndex.phases[longestOpPhaseIndex].pressureResultsTable.rows[l][0];
          const depthIndex = depths.findIndex(x => x == currentDepth);

          if (depths[depthIndex] == currentDepth) {
            const rowIndex = allOperationResultsPhaseAtIndex[0].pressureResultsTable.rows.findIndex(x => x[0] == currentDepth);
            // lastPhaseNew = allOperationsResults[i].phases.length - 1;
            const rows = allOperationResultsPhaseAtIndex[lastPhaseNew].pressureResultsTable.rows;
            if (l < rows.length && rowIndex >= 0 &&
              currentDepth <= rows[rows.length - 1][0]) {
              multipleResults.push(rows[rowIndex][annulusColumnIndex]);
            } else if (currentDepth <= rows[rows.length - 1][0]) {
              const currentOperationDepths = rows.map(x => x[0]);
              const previousDepthIndex = currentOperationDepths.findIndex(x => x >= currentDepth) - 1;

              if (previousDepthIndex < 0) {
                multipleResults.push(null);
                continue;
              }

              const nextDepthIndex = currentOperationDepths.findIndex(x => x >= currentDepth);

              const previousDepth = currentOperationDepths[previousDepthIndex];
              const nextDepth = currentOperationDepths[nextDepthIndex];

              const previousPress = rows[previousDepthIndex][annulusColumnIndex];
              const nextPress = rows[nextDepthIndex][annulusColumnIndex];

              // let currentOperationPressures = allOperationsResults[i].phases[lastPhase].pressureResultsTable.rows.map(x => x[annulusColumnIndex]);

              // let interpoaltedPressure = LinearInterpSingleVal(currentDepth, currentOperationDepths, currentOperationPressures);

              const interpolatedPressure = previousPress + (nextPress - previousPress) / (nextDepth - previousDepth) * (currentDepth - previousDepth);

              multipleResults.push(interpolatedPressure);
            } else {
              multipleResults.push(null);
            }
          }
        } else {
          multipleResults.push(null);
        }
      }

      if (l === allOperationsResults[operationCount].phases[allOperationsResults[operationCount].phases.length - 1].pressureResultsTable.rows.length - 1) {
        operationCount++;
      }

      this.multipleResultsFull.push(multipleResults);
    }

    if (this.selectedSingleMultiple === 'multipleOperation') {
      this.operationNames = this.operationNames.filter(ar => allOperationsResults.find(rm => (ar.header.startsWith(rm.name))));
    }

    this.operationNames.unshift({ field: 'tvdFt', header: `TVD (${this.depthUnit})`, valueFormatter: (params: any) => this.formatDecimal(params) });
    this.operationNames.unshift({ field: 'mdFt', header: `MD (${this.depthUnit})`, valueFormatter: (params: any) => this.formatDecimal(params) });

    this.resultsGrid = this.multipleResultsFull.map(r => zipObject(this.operationNames.map(c => c.field), r));
  }

  private remapResults(selectedPhase?: PressureResultPhase): void {
    let selectedOperationResults = this.allOperationsResults.find(o => o.operationId === this.selectedOperation.id);
    if (!selectedOperationResults) {
      selectedOperationResults = this.allOperationsResults.find(o => o.operationId === (this.selectedOperation['operationId'] ?? this.selectedOperation['value']?.id ?? this.selectedOperation.id));
    }
    this.needToCalculate = !selectedOperationResults;
    this.phases = selectedOperationResults ? selectedOperationResults.phases : null;
    if (selectedPhase == undefined || !selectedPhase) {
      selectedPhase = this.phases ? this.phases[this.phases.length - 1] : null;
    }
    if (selectedPhase) {
      this.results = selectedPhase.pressureResultsTable.rows;
      if (isNaN(this.results[0][3])) {
        this.results = [];
        this.cols = [];
        this.resultsGrid = [];
        this.needToCalculate = true;
        // this.calculationError = selectedOperationResults['errors'][1] ?? "PERICAL engine error while calculating operation.";
        this.calculationError = "PERICAL engine error while calculating operation.";
        // this.calculationError = "PERICAL engine error while calculating operation.";
        return;
      }
      this.cols = selectedPhase.pressureResultsTable.headers.map(h => {
        return {
          field: toCamelCaseString((h === `MD (${this.depthUnit})` || h === `TVD (${this.depthUnit})`) ? h.split(' ').shift() : h),
          header: h,
          valueFormatter: (params: any) => this.formatDecimal(params)
        };
      });
      this.resultsGrid = this.results.map(r => zipObject(this.cols.map(c => c.field), r));
    } else {
      // this.calculationError = selectedOperationResults['errors'][1] ?? (!this.needToCalculate ? "PERICAL engine error while calculating operation." : "Please calculate PERICAL to see results.");
      this.calculationError = !this.needToCalculate ? "PERICAL engine error while calculating operation." : "Please calculate PERICAL to see results.";
      // this.calculationError = "PERICAL engine error while calculating operation.";
      this.needToCalculate = true;
      this.results = [];
      this.cols = [];
      this.resultsGrid = [];
    }
  }

  private formatDecimal(value: any) {
    return value || value === 0
      ? (Math.trunc(value * 100) / 100).toLocaleString('en-US')
      : null;
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
